import React, { useMemo } from "react";
import { Alert, Card, Col, Container, Image, Row, Spinner, Table } from "react-bootstrap";
import { Navigate, Link, useLocation, useParams } from "react-router-dom";
import { PublishedProduct } from "Components/ProductSilde";
import { useGetStoreOrderByIdQuery } from "../../redux/services/store/storeCheckoutApi";
import { useGetStoreMeQuery } from "../../redux/services/store/storeAuthApi";
import { useGetStoreSettingsPublicQuery } from "../../redux/services/store/storeSettingsApi";

import logodark from "assets/images/logo-dark.png";
import Logolight from "assets/images/logo-light.png";

const toNumber = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (amount: unknown, currencyCode = "INR") => {
  const code = String(currencyCode || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(toNumber(amount));
  } catch {
    return `${code} ${toNumber(amount).toFixed(2)}`;
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return {
      date: "-",
      time: "",
    };
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return {
      date: value,
      time: "",
    };
  }

  return {
    date: dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: dt.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const sentenceCase = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const getStatusVariant = (status?: string | null) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "paid" || normalized === "captured") return "success";
  if (normalized === "pending" || normalized === "authorized") return "warning";
  if (normalized === "failed") return "danger";
  return "secondary";
};

const isInvoiceAllowed = (paymentStatus?: string | null) => {
  const normalized = String(paymentStatus || "").toLowerCase();
  return normalized === "paid" || normalized === "captured";
};

const formatAddressLine = (address?: Record<string, any> | null) => {
  if (!address) return "-";
  return [
    address.line1,
    address.line2,
    address.landmark,
    address.city,
    address.state,
    address.postal_code,
    address.country_code,
  ]
    .filter(Boolean)
    .join(", ");
};

const getCustomizationSummary = (item?: {
  customization_enabled?: boolean | null;
  customization_color?: string | null;
  customization_note?: string | null;
}) => {
  const color = String(item?.customization_color || "").trim();
  const note = String(item?.customization_note || "").trim();
  const enabled = Boolean(item?.customization_enabled || color || note);
  if (!enabled) return "";

  const parts = [];
  if (color) parts.push(`Custom color: ${color}`);
  if (note) parts.push(`Custom note: ${note}`);
  return parts.length ? parts.join(" | ") : "Customization requested";
};

const Invoice = () => {
  document.title = "Invoice | Toner - React FrontEnd";

  const location = useLocation();
  const { orderId } = useParams();
  const parsedOrderId = Number(orderId);
  const hasValidOrderId = Number.isFinite(parsedOrderId) && parsedOrderId > 0;

  const {
    data: me,
    isLoading: isMeLoading,
    isFetching: isMeFetching,
    isSuccess: isMeSuccess,
  } = useGetStoreMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCheckingAuth = isMeLoading || isMeFetching;
  const isLoggedIn = isMeSuccess && !!me?.id;

  const {
    data: order,
    isLoading: isOrderLoading,
    isFetching: isOrderFetching,
    isError: isOrderError,
  } = useGetStoreOrderByIdQuery(parsedOrderId, {
    skip: !isLoggedIn || !hasValidOrderId,
    refetchOnMountOrArgChange: true,
  });

  const { data: publicSettings } = useGetStoreSettingsPublicQuery(undefined, {
    skip: !isLoggedIn,
  });

  const placed = formatDateTime(order?.placed_at);
  const billingAddress = (order?.billing_address_snapshot_json || {}) as Record<string, any>;
  const shippingAddress = (order?.shipping_address_snapshot_json || {}) as Record<string, any>;
  const latestPayment = useMemo(() => {
    const payments = Array.isArray(order?.payments) ? order.payments : [];
    return payments.length ? payments[payments.length - 1] : null;
  }, [order?.payments]);

  const storeName = publicSettings?.store?.name || "Store";
  const supportEmail = publicSettings?.store?.support_email || "-";
  const supportPhone = publicSettings?.store?.support_phone || "-";
  const supportCountry = publicSettings?.store?.country_code || "-";
  const logoUrl = publicSettings?.header_logo_url || publicSettings?.footer_logo_url || null;
  const itemCount = Array.isArray(order?.items) ? order.items.length : 0;
  const canViewInvoice = isInvoiceAllowed(order?.payment_status);

  const handlePrint = () => {
    window.print();
  };

  if (isCheckingAuth) {
    return (
      <React.Fragment>
        <PublishedProduct title="Invoice" dicription="Loading order invoice" />
        <section className="section pt-0">
          <Container>
            <div className="d-flex align-items-center justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </Container>
        </section>
      </React.Fragment>
    );
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/auth-signin-basic"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  if (!hasValidOrderId) {
    return <Navigate to="/shop/orderhistory" replace />;
  }

  return (
    <React.Fragment>
      <PublishedProduct title="Invoice" dicription={order?.order_no || "Order invoice"} />
      <section className="section pt-0">
        <Container>
          {isOrderError ? (
            <Alert variant="danger">Unable to load this invoice.</Alert>
          ) : isOrderLoading || isOrderFetching || !order ? (
            <Card className="term-card mb-0">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" />
                <div className="mt-3 text-muted">Loading invoice...</div>
              </Card.Body>
            </Card>
          ) : !canViewInvoice ? (
            <Alert variant="warning" className="mb-0">
              Invoice is available only after payment is completed.
            </Alert>
          ) : (
            <Row>
              <Col lg={12}>
                <Card className="term-card mb-0">
                  <Row>
                    <Col lg={12}>
                      <Card.Header className="border-bottom-dashed p-4">
                        <div className="d-sm-flex">
                          <div className="flex-grow-1">
                            {logoUrl ? (
                              <>
                                <Image
                                  src={logoUrl}
                                  className="card-logo card-logo-dark"
                                  alt={storeName}
                                  height="32"
                                />
                                <Image
                                  src={logoUrl}
                                  className="card-logo card-logo-light"
                                  alt={storeName}
                                  height="32"
                                />
                              </>
                            ) : (
                              <>
                                <Image src={logodark} className="card-logo card-logo-dark" alt="logo dark" height="26" />
                                <Image src={Logolight} className="card-logo card-logo-light" alt="logo light" height="26" />
                              </>
                            )}
                            <div className="mt-sm-5 mt-4">
                              <h6 className="text-muted mb-2 text-uppercase fw-semibold fs-14">
                                Shipping Address
                              </h6>
                              <p className="text-muted mb-1">{formatAddressLine(shippingAddress)}</p>
                              <p className="text-muted mb-0">
                                <span>Country:</span> {shippingAddress.country_code || supportCountry}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 mt-sm-0 mt-3">
                            <h6>
                              <span className="text-muted fw-normal">Store:</span> {storeName}
                            </h6>
                            <h6>
                              <span className="text-muted fw-normal">Email:</span> {supportEmail}
                            </h6>
                            <h6>
                              <span className="text-muted fw-normal">Country:</span> {supportCountry}
                            </h6>
                            <h6 className="mb-0">
                              <span className="text-muted fw-normal">Contact No:</span> {supportPhone}
                            </h6>
                          </div>
                        </div>
                      </Card.Header>
                    </Col>

                    <Col lg={12}>
                      <Card.Body className="p-4">
                        <Row className="g-3">
                          <Col xs={6} lg={3}>
                            <p className="text-muted mb-2 text-uppercase fw-semibold fs-14">Invoice No</p>
                            <h5 className="fs-15 mb-0">{order.order_no}</h5>
                          </Col>
                          <Col xs={6} lg={3}>
                            <p className="text-muted mb-2 text-uppercase fw-semibold fs-14">Date</p>
                            <h5 className="fs-15 mb-0">
                              {placed.date}
                              {placed.time ? <small className="text-muted ms-2">{placed.time}</small> : null}
                            </h5>
                          </Col>
                          <Col xs={6} lg={3}>
                            <p className="text-muted mb-2 text-uppercase fw-semibold fs-14">Payment Status</p>
                            <span className={`badge bg-${getStatusVariant(order.payment_status)}-subtle text-${getStatusVariant(order.payment_status)}`}>
                              {sentenceCase(order.payment_status)}
                            </span>
                          </Col>
                          <Col xs={6} lg={3}>
                            <p className="text-muted mb-2 text-uppercase fw-semibold fs-14">Total Amount</p>
                            <h5 className="fs-15 mb-0">{formatCurrency(order.grand_total, order.currency_code)}</h5>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Col>

                    <Col lg={12}>
                      <Card.Body className="p-4 border-top border-top-dashed">
                        <Row className="g-3">
                          <Col xs={6}>
                            <h6 className="text-muted text-uppercase fw-semibold fs-14 mb-3">Billing Address</h6>
                            <p className="fw-medium mb-2 fs-16">{billingAddress.contact_name || "-"}</p>
                            <p className="text-muted mb-1">{formatAddressLine(billingAddress)}</p>
                            <p className="text-muted mb-0">
                              <span>Phone:</span> {billingAddress.mobile_e164 || "-"}
                            </p>
                          </Col>

                          <Col xs={6}>
                            <h6 className="text-muted text-uppercase fw-semibold fs-14 mb-3">Shipping Address</h6>
                            <p className="fw-medium mb-2 fs-16">{shippingAddress.contact_name || "-"}</p>
                            <p className="text-muted mb-1">{formatAddressLine(shippingAddress)}</p>
                            <p className="text-muted mb-0">
                              <span>Phone:</span> {shippingAddress.mobile_e164 || "-"}
                            </p>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Col>

                    <Col lg={12}>
                      <Card.Body className="p-4">
                        <div className="table-responsive">
                          <Table className="table-borderless text-center table-nowrap align-middle mb-0">
                            <thead>
                              <tr className="table-active">
                                <th scope="col" style={{ width: "50px" }}>#</th>
                                <th scope="col">Product Details</th>
                                <th scope="col">Rate</th>
                                <th scope="col">Quantity</th>
                                <th scope="col" className="text-end">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, index) => (
                                <tr key={item.id}>
                                  <th scope="row">{index + 1}</th>
                                  <td className="text-start">
                                    <span className="fw-medium">{item.product_name_snapshot}</span>
                                    <p className="text-muted mb-0">
                                      {[
                                        item.variant_name_snapshot || item.sku_snapshot || "",
                                        getCustomizationSummary(item),
                                      ]
                                        .filter(Boolean)
                                        .join(" | ") || "-"}
                                    </p>
                                  </td>
                                  <td>{formatCurrency(item.unit_price, order.currency_code)}</td>
                                  <td>{toNumber(item.qty)}</td>
                                  <td className="text-end">
                                    {formatCurrency(item.line_total, order.currency_code)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <div className="border-top border-top-dashed mt-2">
                          <Table
                            className="table-borderless table-nowrap align-middle mb-0 ms-auto"
                            style={{ width: "280px" }}
                          >
                            <tbody>
                              <tr>
                                <td>Sub Total</td>
                                <td className="text-end">{formatCurrency(order.subtotal, order.currency_code)}</td>
                              </tr>
                              <tr>
                                <td>Tax</td>
                                <td className="text-end">{formatCurrency(order.tax_total, order.currency_code)}</td>
                              </tr>
                              <tr>
                                <td>Discount</td>
                                <td className="text-end">
                                  - {formatCurrency(order.discount_total, order.currency_code)}
                                </td>
                              </tr>
                              <tr>
                                <td>Shipping Charge</td>
                                <td className="text-end">{formatCurrency(order.shipping_total, order.currency_code)}</td>
                              </tr>
                              <tr className="border-top border-top-dashed fs-15">
                                <th scope="row">Total Amount</th>
                                <th className="text-end">{formatCurrency(order.grand_total, order.currency_code)}</th>
                              </tr>
                            </tbody>
                          </Table>
                        </div>

                        <div className="mt-3">
                          <h6 className="text-muted text-uppercase fw-semibold mb-3">Payment Details</h6>
                          <p className="text-muted mb-1">
                            Payment Method:{" "}
                            <span className="fw-medium">{sentenceCase(latestPayment?.method || "pending")}</span>
                          </p>
                          <p className="text-muted mb-1">
                            Payment Provider:{" "}
                            <span className="fw-medium">{sentenceCase(latestPayment?.provider || "-")}</span>
                          </p>
                          <p className="text-muted mb-1">
                            Payment Status:{" "}
                            <span className="fw-medium">{sentenceCase(order.payment_status)}</span>
                          </p>
                          <p className="text-muted mb-0">
                            Items: <span className="fw-medium">{itemCount}</span>
                          </p>
                        </div>

                        {order.notes ? (
                          <div className="mt-4">
                            <Alert variant="info" className="mb-0">
                              <p className="mb-0">
                                <span className="fw-semibold">Notes:</span> {order.notes}
                              </p>
                            </Alert>
                          </div>
                        ) : null}

                        <div className="hstack gap-2 justify-content-end d-print-none mt-4">
                          <button type="button" className="btn btn-success" onClick={handlePrint}>
                            <i className="ri-printer-line align-bottom me-1"></i> Print
                          </button>
                          <Link to="/shop/orderhistory" className="btn btn-primary">
                            Back to Orders
                          </Link>
                        </div>
                      </Card.Body>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </section>
    </React.Fragment>
  );
};

export default Invoice;
